import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { MapContainer, TileLayer, Marker, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  ArrowLeft, Package, BadgeCheck, Truck, CheckCircle2, Loader2, Search,
  Route, Clock3, Car, Box, CalendarClock, Wallet,
} from "lucide-react";
import { API, BRAND } from "../constants/brand";
import { formatEUR } from "../context/CartContext";

const STEPS = [
  { id: "gebucht", label: "Gebucht", icon: Package },
  { id: "bestätigt", label: "Bestätigt", icon: BadgeCheck },
  { id: "unterwegs", label: "Unterwegs", icon: Truck },
  { id: "abgeschlossen", label: "Zugestellt", icon: CheckCircle2 },
];

const VEHICLE_NAMES = { transit: "Ford Transit Kasten", movano: "Opel Movano", crafter: "VW Crafter" };
const CARGO_LABELS = { express: "Express-Paket", pakete: "Pakete & Kartons", paletten: "Palettenware" };

const inputCls =
  "w-full rounded-xl bg-[#0B0F17]/80 border border-white/10 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-cyan-400/50 transition-colors";

const dotIcon = (color, label) =>
  L.divIcon({
    className: "",
    html: `<div style="width:30px;height:30px;border-radius:50%;background:${color};border:3px solid #0B0F17;box-shadow:0 0 18px ${color};display:flex;align-items:center;justify-content:center;color:#0B0F17;font-weight:800;font-size:13px;font-family:Outfit,sans-serif;">${label}</div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
  });

const shortLabel = (displayName) => displayName.split(",").slice(0, 2).join(",").trim();

async function geocode(q) {
  let res = await fetch(
    `https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=de&q=${encodeURIComponent(q)}`
  );
  let data = await res.json();
  if (!data.length) {
    res = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=de&q=${encodeURIComponent(q + ", Deutschland")}`
    );
    data = await res.json();
  }
  if (!data.length) throw new Error("not found");
  return { lat: parseFloat(data[0].lat), lon: parseFloat(data[0].lon), label: shortLabel(data[0].display_name) };
}

const FitBounds = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (points.length === 2) {
      map.fitBounds(points.map((p) => [p.lat, p.lon]), { padding: [48, 48] });
    }
  }, [points, map]);
  return null;
};

const fmtWhen = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" }) + " Uhr";
};

const DetailItem = ({ icon: Icon, label, value, testid }) => (
  <div className="rounded-xl border border-white/5 bg-white/[0.02] px-4 py-3">
    <div className="flex items-center gap-1.5 text-[10px] font-mono-tech uppercase tracking-[0.2em] text-slate-500">
      <Icon size={12} className="text-cyan-400/80" /> {label}
    </div>
    <div data-testid={testid} className="mt-1 text-sm text-slate-100 font-medium">{value}</div>
  </div>
);

export default function TrackingPage() {
  const [params] = useSearchParams();
  const [tn, setTn] = useState(params.get("tn") || "");
  const [email, setEmail] = useState(params.get("email") || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [points, setPoints] = useState([]);
  const autoSubmitted = useRef(false);

  const submit = async (t, e) => {
    setLoading(true);
    setError("");
    setData(null);
    setPoints([]);
    try {
      const { data: res } = await axios.get(`${API}/track`, { params: { order_no: t, email: e } });
      setData(res);
      try {
        const [a, b] = await Promise.all([geocode(res.pickup), geocode(res.dropoff)]);
        setPoints([a, b]);
      } catch {
        /* Karte optional */
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Kein Transport gefunden. Bitte Eingaben prüfen.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    const t = params.get("tn");
    const e = params.get("email");
    if (t && e && !autoSubmitted.current) {
      autoSubmitted.current = true;
      submit(t, e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stepIndex = data ? STEPS.findIndex((s) => s.id === data.status) : -1;
  const stepTime = (id) => {
    const entry = (data?.status_history || []).find((h) => h.status === id);
    return entry ? fmtWhen(entry.at) : null;
  };

  return (
    <div data-testid="tracking-page" className="min-h-screen bg-[#0A0E14] text-slate-200">
      <header className="border-b border-white/5">
        <div className="max-w-3xl mx-auto px-5 py-6 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <img src="/logo-mark.png" alt={`${BRAND.name} Logo`} className="w-9 h-9 rounded-lg object-cover" />
            <div>
              <div className="font-semibold text-white text-sm">{BRAND.name}</div>
              <div className="text-[10px] tracking-[0.25em] text-slate-500 font-mono-tech">{BRAND.tagline}</div>
            </div>
          </a>
          <a
            data-testid="tracking-back-link"
            href="/"
            className="flex items-center gap-2 text-xs text-slate-400 hover:text-cyan-300 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Zurück zur Startseite
          </a>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-12 lg:py-16">
        <div className="text-[10px] font-mono-tech uppercase tracking-[0.3em] text-cyan-400 mb-3">Sendungsverfolgung · A nach B</div>
        <h1 className="text-4xl font-bold text-white mb-3">Wo ist meine Sendung?</h1>
        <p className="text-sm text-slate-400 mb-10 leading-relaxed">
          Tour-Nr. (z.&nbsp;B. TR-2026-0001) und deine E-Mail-Adresse eingeben — du siehst sofort den aktuellen Status deiner Sendung.
        </p>

        <form
          data-testid="tracking-form"
          onSubmit={(ev) => {
            ev.preventDefault();
            submit(tn, email);
          }}
          className="rounded-2xl border border-white/5 bg-white/[0.02] p-6 space-y-4"
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <input
              data-testid="tracking-input-tn"
              value={tn}
              onChange={(e) => setTn(e.target.value)}
              placeholder="Tour-Nr. (TR-…)"
              required
              className={`${inputCls} font-mono-tech`}
            />
            <input
              data-testid="tracking-input-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="E-Mail-Adresse"
              required
              className={inputCls}
            />
          </div>
          <button
            data-testid="tracking-submit"
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#1E6FD9] to-[#29B6F6] px-4 py-3 text-sm font-semibold text-white hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
            Sendung verfolgen
          </button>
        </form>

        {error && (
          <div data-testid="tracking-error" className="mt-6 rounded-2xl border border-red-400/20 bg-red-400/5 p-5 text-sm text-red-300">
            {error}
          </div>
        )}

        {data && (
          <div data-testid="tracking-result" className="mt-10 space-y-6">
            <div className="rounded-2xl border border-cyan-400/20 bg-cyan-400/5 p-6">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <div>
                  <div className="font-mono-tech text-[10px] text-cyan-400/90 tracking-widest">{data.order_no}</div>
                  <div className="text-lg font-semibold text-white">Hallo {data.name}, hier ist dein Status.</div>
                </div>
                <span data-testid="tracking-current-status" className="rounded-full border border-cyan-400/30 bg-cyan-400/10 px-3 py-1.5 text-[10px] font-mono-tech uppercase tracking-widest text-cyan-300">
                  {STEPS[stepIndex]?.label || data.status}
                </span>
              </div>

              <div data-testid="tracking-timeline" className="flex flex-col sm:flex-row sm:items-start gap-6 sm:gap-0">
                {STEPS.map((s, i) => {
                  const reached = i <= stepIndex;
                  const current = i === stepIndex;
                  const time = stepTime(s.id);
                  return (
                    <div key={s.id} data-testid={`tracking-step-${s.id}`} className="flex sm:flex-col items-center sm:flex-1 gap-3 sm:gap-2 relative">
                      {i < STEPS.length - 1 && (
                        <div className={`hidden sm:block absolute top-[18px] left-1/2 w-full h-0.5 ${i < stepIndex ? "bg-cyan-400/60" : "bg-white/10"}`} />
                      )}
                      <div
                        className={`relative z-10 w-9 h-9 rounded-full flex items-center justify-center shrink-0 border ${
                          reached
                            ? "bg-gradient-to-br from-[#1E6FD9] to-[#29B6F6] border-cyan-300/40 text-white"
                            : "bg-white/[0.03] border-white/10 text-slate-600"
                        } ${current ? "ring-4 ring-cyan-400/20 animate-pulse" : ""}`}
                      >
                        <s.icon size={16} />
                      </div>
                      <div className="sm:text-center">
                        <div className={`text-xs font-semibold ${reached ? "text-slate-100" : "text-slate-600"}`}>{s.label}</div>
                        {time && <div className="text-[10px] text-slate-500 font-mono-tech mt-0.5">{time}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <DetailItem icon={Route} label="Route" value={`${data.pickup} → ${data.dropoff}`} testid="tracking-route" />
              <DetailItem icon={Clock3} label="Distanz / Dauer" value={`${data.distance_km} km${data.duration_min ? ` · ca. ${Math.round(data.duration_min)} Min.` : ""}`} testid="tracking-distance" />
              <DetailItem icon={Car} label="Fahrzeug" value={VEHICLE_NAMES[data.vehicle] || data.vehicle} testid="tracking-vehicle" />
              <DetailItem icon={Box} label="Ladung" value={CARGO_LABELS[data.cargo_type] || data.cargo_type || "—"} testid="tracking-cargo" />
              {data.scheduled_at && (
                <DetailItem icon={CalendarClock} label="Geplant für" value={fmtWhen(data.scheduled_at)} testid="tracking-scheduled" />
              )}
              <DetailItem
                icon={Wallet}
                label="Preis"
                value={data.is_free ? "KOSTENLOS (11. Transport)" : formatEUR(data.price_eur)}
                testid="tracking-price"
              />
            </div>

            {points.length === 2 && (
              <div data-testid="tracking-map" className="rounded-2xl overflow-hidden border border-white/10 h-72">
                <MapContainer center={[points[0].lat, points[0].lon]} zoom={10} className="h-full w-full" scrollWheelZoom={false}>
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <FitBounds points={points} />
                  <Marker position={[points[0].lat, points[0].lon]} icon={dotIcon("#29B6F6", "A")} />
                  <Marker position={[points[1].lat, points[1].lon]} icon={dotIcon("#10B981", "B")} />
                  <Polyline positions={points.map((p) => [p.lat, p.lon])} pathOptions={{ color: "#29B6F6", weight: 3, dashArray: "6 8" }} />
                </MapContainer>
              </div>
            )}

            <p className="text-xs text-slate-500 leading-relaxed">
              Fragen zu deiner Sendung? Erreich uns unter{" "}
              <a href={`tel:${BRAND.phone}`} className="text-cyan-300 hover:underline">{BRAND.phone}</a> oder per WhatsApp — einfach die Tour-Nr. bereithalten.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
